import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription, skip } from 'rxjs';
import { Util } from 'app/models/util';
import { DesktopAgentService } from 'app/services/desktop-agent.service';
import { PermissionsService } from 'app/modules/admin/apps/file-manager/permissions/permissions.service';
import { ActionDirective } from '@fuse/directives/action.directive';

/**
 * Dismissable banner advertising the desktop agent.
 */
@Component({
    selector: 'desktop-agent-banner',
    templateUrl: './desktop-agent-banner.component.html',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        ActionDirective,
    ],
})
export class DesktopAgentBannerComponent implements OnInit, OnDestroy {
    visible = false;

    private agentInstalledSub: Subscription;

    constructor(
        private util: Util,
        private desktopAgentService: DesktopAgentService,
        private permissionsService: PermissionsService,
    ) {}

    async ngOnInit(): Promise<void> {
        // Same gate the 'openOnDesktop' action uses - single source of truth for the feature flag.
        if (!this.permissionsService.isOpenOnDesktopEnabled()) {
            return;
        }

        if (this.util.isDesktopAgentBannerDismissed()) {
            return;
        }

        // Skip cached value; fresh probe decides.
        let detectedDuringProbe = false;
        this.agentInstalledSub = this.desktopAgentService.agentInstalled$.pipe(skip(1)).subscribe((installed) => {
            if (installed) {
                detectedDuringProbe = true;
                this.visible = false;
            }
        });

        const alreadyInstalled = await this.desktopAgentService.checkAlreadyInstalled();
        this.visible = !alreadyInstalled && !detectedDuringProbe;
    }

    ngOnDestroy(): void {
        this.agentInstalledSub?.unsubscribe();
    }

    install(): void {
        this.desktopAgentService.openInstallModal();
    }

    dismiss(): void {
        this.visible = false;
        this.util.dismissDesktopAgentBanner();
    }
}
